// =============================================================================
// PictoEnioAnime.js — Pictogramme Enio animé « vague de profondeur »
// =============================================================================
// Les 17 losanges du pictogramme officiel (géométrie EXACTE de PICTO ENIO.svg :
// mêmes rects, mêmes translate/rotate(135), même ordre d'empilement, multiply
// isolé comme l'original) flottent en profondeur de champ : une onde se propage
// du centre vers l'extérieur, chaque losange creuse vers l'arrière-plan
// (rétrécit, s'estompe, se floute) puis surgit au premier plan (grossit,
// redevient net, ombre marquée) avant de se reposer. Les GRANDS losanges
// voyagent plus loin que les petits ; période commune 6,8 s pour que la vague
// ne se délite jamais, mais rythmes désolidarisés (déphasage irrégulier,
// courbes d'accélération propres, dérive x/y indépendante).
//
// Paramètres par losange PRÉ-CALCULÉS en littéraux (pas de var() dans les
// @keyframes : les Safari < 15.4 — iPad — les résolvent mal). Les ombres
// portées Illustrator d'origine (filtres à région userSpaceOnUse FIXE, qui
// rognent le losange dès qu'on le scale) sont remplacées par des drop-shadow
// CSS animées avec la profondeur. viewBox élargi (-70) : rien n'est coupé au
// maximum d'échelle. `prefers-reduced-motion` → picto statique.
//
// Usage : <PictoEnioAnime width="min(var(--vw100) * 0.68,380px)"/> — inline uniquement
// (en <img>, Chrome fige les animations CSS d'un SVG).
// =============================================================================

import { useEffect, useState } from "react";

// Rayon des coins, COMMUN aux 17 losanges, dans le repère du viewBox
// (882 x 882). Le « Picto Enio SVG.svg » de la charte porte l'ancienne forme
// aux coins quasi vifs (rx 5,67) : ne pas le reprendre. Toute autre copie du
// picto en dérive (UNLOCK_PIECES, splash de public/index.html, SVG publics,
// app iOS, site carrières) ; verrou : src/pictoCoinsArrondis.test.js.
export const RAYON_COINS = 22.5;

// x/y/w/tx/ty : géométrie d'origine — delay/far/near/farO/farB/nearSh/ease :
// onde — dx/dy/ddur/ddelay : dérive lente.
export const LOSANGES = [
  {fill:"#95c11f",x:353.07,y:675.87,w:172.87,tx:1289.3185,ty:990.5552,
   delay:-0.76,far:0.817,near:1.134,farO:0.53,farB:3.7,nearSh:0.27,
   ease:"cubic-bezier(.45,.05,.55,.95)",dx:5.5,dy:-9.1,ddur:10,ddelay:-4.1},
  {fill:"#95c11f",x:549.66,y:228.06,w:98.54,tx:1218.5423,ty:49.9245,
   delay:-2.81,far:0.899,near:1.071,farO:0.68,farB:2.2,nearSh:0.21,
   ease:"cubic-bezier(.5,0,.24,1)",dx:-2.1,dy:3.2,ddur:13.7,ddelay:-1.1},
  {fill:"#02938a",x:617.23,y:347.42,w:187.74,tx:1525.9702,ty:250.508,
   delay:-2.49,far:0.801,near:1.146,farO:0.5,farB:4,nearSh:0.29,
   ease:"cubic-bezier(.6,-0.02,.35,1)",dx:11.4,dy:-4.1,ddur:11.4,ddelay:-6},
  {fill:"#36a9e1",x:306.72,y:608.27,w:73.66,tx:1042.6356,ty:858.3324,
   delay:-2.57,far:0.927,near:1.05,farO:0.74,farB:1.6,nearSh:0.19,
   ease:"cubic-bezier(.45,.05,.55,.95)",dx:1.3,dy:5.8,ddur:9.1,ddelay:-3},
  {fill:"#cbbba0",x:356.13,y:195,w:215.66,tx:1006.1671,ty:188.8921,
   delay:-4.48,far:0.77,near:1.17,farO:0.44,farB:4.6,nearSh:0.31,
   ease:"cubic-bezier(.32,.08,.68,.92)",dx:-7.3,dy:1.6,ddur:12.8,ddelay:-7.9},
  {fill:"#cbbba0",x:20.16,y:388.34,w:108.69,tx:440.22,ty:703.03,
   delay:-0.97,far:0.888,near:1.08,farO:0.66,farB:2.4,nearSh:0.22,
   ease:"cubic-bezier(.6,-0.02,.35,1)",dx:5.5,dy:-5.2,ddur:10.5,ddelay:-4.9},
  {fill:"#f49800",x:256.89,y:398.35,w:187.74,tx:946.83,ty:592.24,
   delay:-4.85,far:0.801,near:1.146,farO:0.5,farB:4,nearSh:0.29,
   ease:"cubic-bezier(.5,0,.24,1)",dx:-1,dy:7,ddur:8.2,ddelay:-1.8},
  {fill:"#36a9e1",x:360.78,y:31.58,w:163.82,tx:835.96,ty:-119.29,
   delay:-1.39,far:0.827,near:1.126,farO:0.55,farB:3.5,nearSh:0.27,
   ease:"cubic-bezier(.32,.08,.68,.92)",dx:-9.1,dy:-1.8,ddur:11.9,ddelay:-6.8},
  {fill:"#e20a17",x:796.9,y:407.12,w:73.02,tx:1736.4092,ty:168.0213,
   delay:0.41,far:0.927,near:1.05,farO:0.74,farB:1.6,nearSh:0.19,
   ease:"cubic-bezier(.45,.05,.55,.95)",dx:2.5,dy:-6.1,ddur:9.6,ddelay:-3.7},
  {fill:"#d60b52",x:192.53,y:206.63,w:140.6,tx:644.5,ty:286.89,
   delay:-2.6,far:0.853,near:1.107,farO:0.6,farB:3,nearSh:0.25,
   ease:"cubic-bezier(.5,0,.24,1)",dx:-3.6,dy:2.9,ddur:13.3,ddelay:-0.6},
  {fill:"#ec6608",x:570.52,y:545.57,w:105.64,tx:1487.2345,ty:580.7534,
   delay:-3.06,far:0.891,near:1.077,farO:0.67,farB:2.3,nearSh:0.22,
   ease:"cubic-bezier(.6,-0.02,.35,1)",dx:6.9,dy:-3.7,ddur:11.1,ddelay:-5.6},
  {fill:"#951b81",x:168.56,y:468.72,w:152.05,tx:802.72,ty:756.99,
   delay:-2.69,far:0.84,near:1.116,farO:0.57,farB:3.3,nearSh:0.26,
   ease:"cubic-bezier(.45,.05,.55,.95)",dx:0.9,dy:7.8,ddur:8.8,ddelay:-2.5},
  {fill:"#951b81",x:559.1,y:167.82,w:79.67,tx:1169.2737,ty:-69.0203,
   delay:-2.09,far:0.92,near:1.055,farO:0.72,farB:1.8,nearSh:0.2,
   ease:"cubic-bezier(.32,.08,.68,.92)",dx:-4.6,dy:0.1,ddur:12.5,ddelay:-7.5},
  {fill:"#02938a",x:115.72,y:362.98,w:98.61,tx:573.24,ty:587.13,
   delay:-1.47,far:0.899,near:1.071,farO:0.68,farB:2.2,nearSh:0.21,
   ease:"cubic-bezier(.6,-0.02,.35,1)",dx:4.3,dy:-5.8,ddur:10.2,ddelay:-4.4},
  {fill:"#02938a",x:475.82,y:676.13,w:61.66,tx:1364.7989,ty:848.6022,
   delay:-1.96,far:0.94,near:1.04,farO:0.76,farB:1.4,nearSh:0.18,
   ease:"cubic-bezier(.5,0,.24,1)",dx:-1.2,dy:2.9,ddur:13.9,ddelay:-1.4},
  {fill:"#e71d73",x:458.65,y:541.2,w:133.21,tx:1326.4471,ty:666.1728,
   delay:-3.89,far:0.861,near:1.1,farO:0.61,farB:2.9,nearSh:0.24,
   ease:"cubic-bezier(.32,.08,.68,.92)",dx:-8.9,dy:-2.6,ddur:11.6,ddelay:-6.3},
  {fill:"#e20a17",x:389.48,y:312.91,w:210.54,tx:1140.2998,ty:364.0412,
   delay:-5.44,far:0.776,near:1.166,farO:0.45,farB:4.5,nearSh:0.31,
   ease:"cubic-bezier(.45,.05,.55,.95)",dx:3.5,dy:12.2,ddur:9.3,ddelay:-3.3},
];

const DUR = 6.8; // période commune de l'onde (s)

// CSS généré une fois au chargement du module : une paire de @keyframes
// littérales par losange (onde + dérive) → aucun var() dans les keyframes.
const PEA_CSS =
  `.pea-scene{isolation:isolate}` +
  `.pea-d{mix-blend-mode:multiply;transform-box:fill-box;transform-origin:center;` +
  `will-change:transform,opacity,filter}` +
  LOSANGES.map((d, i) =>
    `.pea-d${i}{animation:pea-onde-${i} ${DUR}s ${d.ease} ${d.delay}s infinite}` +
    `.pea-f${i}{animation:pea-derive-${i} ${d.ddur}s ease-in-out ${d.ddelay}s infinite alternate}` +
    `@keyframes pea-onde-${i}{` +
      `0%,14%,88%,100%{transform:scale(1);opacity:.97;` +
        `filter:blur(.4px) drop-shadow(0 3px 5px rgba(30,30,28,.16))}` +
      `38%{transform:scale(${d.far});opacity:${d.farO};` +
        `filter:blur(${d.farB}px) drop-shadow(0 1px 2px rgba(30,30,28,.1))}` +
      `60%{transform:scale(${d.near});opacity:1;` +
        `filter:blur(0px) drop-shadow(0 12px 18px rgba(30,30,28,${d.nearSh}))}}` +
    `@keyframes pea-derive-${i}{from{transform:translate(0,0)}` +
      `to{transform:translate(${d.dx}px,${d.dy}px)}}`
  ).join("") +
  `@media (prefers-reduced-motion: reduce){` +
    `.pea-d,[class*="pea-f"]{animation:none !important}` +
    `.pea-d{opacity:1;filter:none}}`;

const PictoEnioAnime = ({ width = "100%", style }) => (
  <svg viewBox="-70 -70 1022.69 1022.2" aria-hidden="true"
    style={{ width, display: "block", ...style }}>
    <style>{PEA_CSS}</style>
    <g className="pea-scene">
      {LOSANGES.map((d, i) => (
        <g key={i} className={`pea-d pea-d${i}`}>
          <g className={`pea-f${i}`}>
            <rect fill={d.fill} x={d.x} y={d.y} width={d.w} height={d.w}
              rx={RAYON_COINS} ry={RAYON_COINS}
              transform={`translate(${d.tx} ${d.ty}) rotate(135)`}/>
          </g>
        </g>
      ))}
    </g>
  </svg>
);

// Pictogramme STATIQUE (lot L7) : aucune animation, même géométrie, même
// empilement, « multiply » isolé comme l'original. Plaque navy de « Ma
// semaine » (en débord, couleurs pleines, règle couleur v2) et sceau « Tout
// est traité » (M12). Décoratif : aria-hidden.
// `teintes` (lot L12d, microsite d'un événement) : 17 couleurs, une par
// losange dans l'ordre de LOSANGES, qui RECOLORENT le pictogramme sans rien
// changer à sa forme (src/mosaiqueEvenement.js) ; posées en style, elles
// acceptent une variable CSS. Sans elles, les couleurs officielles.
export const PictoStatique = ({ taille = 40, className, style, teintes }) => (
  <svg viewBox="0 0 882.69 882.2" width={taille} height={taille} className={className}
    style={style} aria-hidden="true" focusable="false">
    <g style={{ isolation: "isolate" }}>
      {LOSANGES.map((d, i) => (
        <rect key={i} fill={d.fill} x={d.x} y={d.y} width={d.w} height={d.w} rx={RAYON_COINS} ry={RAYON_COINS}
          transform={`translate(${d.tx} ${d.ty}) rotate(135)`}
          style={teintes ? { mixBlendMode: "multiply", fill: teintes[i] } : { mixBlendMode: "multiply" }}/>
      ))}
    </g>
  </svg>
);

// Loader de marque : le picto animé en petit + libellé. Remplace les
// fallbacks Suspense texte (« Chargement… ») des modules lazy — la vague
// démarre en plein vol (délais négatifs), donc vivante même sur un flash
// de quelques centaines de ms.
// Lot L6 : il n'apparaît qu'au-delà de 300 ms (`delai`, 0 pour l'afficher
// d'emblée). Un morceau déjà en cache ne fait plus clignoter le pictogramme ;
// la place est réservée pendant l'attente (aucun saut de mise en page).
export const PictoLoader = ({ label = "Chargement…", size = 110, pad = 28, delai = 300 }) => {
  const [visible, setVisible] = useState(!(delai > 0));
  useEffect(() => {
    if (!(delai > 0)) return undefined;
    const t = setTimeout(() => setVisible(true), delai);
    return () => clearTimeout(t);
  }, [delai]);
  return (
    <div role="status" aria-busy="true" aria-label={label} style={{ padding: pad, display: "flex",
      flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
      minHeight: size + 24 }}>
      {visible && <PictoEnioAnime width={size}/>}
      {visible && <div style={{ fontSize: "var(--fs-micro)", color: "var(--text-sub,#64748B)",
        fontFamily: "Montserrat,Arial,sans-serif", letterSpacing: "calc(.02em * var(--em-ajuste, 1))" }}>{label}</div>}
    </div>
  );
};

export default PictoEnioAnime;
